/**
 * Helper functions for date manipulation and formatting.
 */

import { getYear, getMonth, lastDayOfMonth } from "date-fns";

/* Enum to represent the different date range options in the filters */
export enum DateRangeOption {
  yearToDate = 'yearToDate',
  lastYear = 'lastYear',
  thisQuarter = 'thisQuarter',
  lastQuarter = 'lastQuarter',
  all = 'all',
  customDate = 'customDate',
}

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

/* Helper function to get the date range based on the selected option */
export function getDateRange(option: DateRangeOption, customStart?: Date, customEnd?: Date): DateRange {
  const now = new Date();
  let firstDay: Date | null = null;
  let lastDay: Date | null = null;
  const currentQuarter = Math.ceil((getMonth(now) + 1) / 3);

  switch (option) {
    case DateRangeOption.yearToDate:
      firstDay = new Date(getYear(now), 0, 1);
      lastDay = new Date(getYear(now), 11, 31);
      break;
    case DateRangeOption.lastYear:
      firstDay = new Date(getYear(now) - 1, 0, 1);
      lastDay = new Date(getYear(now) - 1, 11, 31);
      break;
    case DateRangeOption.thisQuarter:
      ({ start: firstDay, end: lastDay } = getQuarterStartAndEndDate(currentQuarter, getYear(now)));
      break;
    case DateRangeOption.lastQuarter:
      const lastQuarter: number = currentQuarter === 1 ? 4 : currentQuarter - 1;
      const year: number = lastQuarter === 4 ? getYear(now) - 1 : getYear(now);
      ({ start: firstDay, end: lastDay } = getQuarterStartAndEndDate(lastQuarter, year));
      break;
    case DateRangeOption.all:
      firstDay = null;
      lastDay = null;
      break;
    case DateRangeOption.customDate:
      firstDay = customStart || null;
      lastDay = customEnd || null;
      break;
  }
  return { start: firstDay, end: lastDay };
}

function getQuarterStartAndEndDate(quarter: number, year: number): DateRange {
  // get the month index for the first month in the quarter
  const firstMonthIndex = 3 * (quarter - 1);
  // get the month index for the last month in the quarter
  const lastMonthIndex = 3 * quarter - 1;

  const firstDay = new Date(year, firstMonthIndex, 1);
  const lastDay = lastDayOfMonth(new Date(year, lastMonthIndex, 1));
  lastDay.setHours(23, 59, 59);

  return {
    start: firstDay,
    end: lastDay,
  };
}